/**
 * Design System - Color Tokens
 * Modern color palette with semantic naming
 */

// Base color scales
export const colors = {
  // Base colors
  white: '#ffffff',
  black: '#000000',
  
  // Neutral grays (updated for better contrast and modern feel)
  gray: {
    50: '#f8fafc',
    100: '#f1f5f9', 
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },

  // Primary blues (sophisticated blue-gray tones)
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe', 
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
    950: '#172554',
  },

  // Secondary indigo (for gradients and accents)
  indigo: {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
    950: '#1e1b4b',
  },

  // Purple (for octane mode and gradients)
  purple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
    950: '#3b0764',
  },

  // Success green
  green: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
    950: '#052e16',
  },

  // Warning amber
  amber: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
    950: '#451a03',
  },

  // Error red
  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a',
  },

  // Special colors for glassmorphism
  glass: {
    white: 'rgba(255, 255, 255, 0.1)',
    whiteHover: 'rgba(255, 255, 255, 0.15)',
    dark: 'rgba(0, 0, 0, 0.1)',
    darkHover: 'rgba(0, 0, 0, 0.15)',
  },
} as const;

// Semantic color tokens for theming
export const semanticColors = {
  light: {
    // Background colors
    background: {
      primary: colors.gray[50],
      secondary: colors.gray[100],
      tertiary: colors.white,
      elevated: colors.white,
      glass: colors.glass.white,
      overlay: 'rgba(255, 255, 255, 0.8)',
    },
    
    // Text colors
    text: {
      primary: colors.gray[900],
      secondary: colors.gray[600],
      tertiary: colors.gray[500],
      inverse: colors.white,
      disabled: colors.gray[400],
    },

    // Border colors
    border: {
      primary: colors.gray[200],
      secondary: colors.gray[100],
      focus: colors.blue[500],
      error: colors.red[500],
    },

    // Interactive colors
    interactive: {
      primary: colors.blue[600],
      primaryHover: colors.blue[700],
      secondary: colors.gray[100],
      secondaryHover: colors.gray[200],
      danger: colors.red[600],
      dangerHover: colors.red[700],
      success: colors.green[600],
      successHover: colors.green[700],
    },

    // Shadow colors
    shadow: {
      sm: 'rgba(0, 0, 0, 0.05)',
      md: 'rgba(0, 0, 0, 0.1)',
      lg: 'rgba(0, 0, 0, 0.15)',
      xl: 'rgba(0, 0, 0, 0.25)',
    },
  },

  dark: {
    // Background colors
    background: {
      primary: colors.gray[900],
      secondary: colors.gray[800],
      tertiary: colors.gray[700],
      elevated: colors.gray[800],
      glass: colors.glass.dark,
      overlay: 'rgba(0, 0, 0, 0.8)',
    },
    
    // Text colors
    text: {
      primary: colors.gray[50],
      secondary: colors.gray[300],
      tertiary: colors.gray[400],
      inverse: colors.gray[900],
      disabled: colors.gray[500],
    },

    // Border colors
    border: {
      primary: colors.gray[700],
      secondary: colors.gray[600],
      focus: colors.blue[400],
      error: colors.red[400],
    },

    // Interactive colors
    interactive: {
      primary: colors.blue[500],
      primaryHover: colors.blue[400],
      secondary: colors.gray[700],
      secondaryHover: colors.gray[600],
      danger: colors.red[500],
      dangerHover: colors.red[400],
      success: colors.green[500],
      successHover: colors.green[400],
    },

    // Shadow colors
    shadow: {
      sm: 'rgba(0, 0, 0, 0.2)',
      md: 'rgba(0, 0, 0, 0.3)',
      lg: 'rgba(0, 0, 0, 0.4)',
      xl: 'rgba(0, 0, 0, 0.6)',
    },
  },
} as const;

// Gradient definitions
export const gradients = {
  primary: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
  secondary: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  warning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
  glassDark: 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.05) 100%)',
} as const;

export type ColorScale = keyof typeof colors;
export type ColorShade = keyof typeof colors.gray;
export type SemanticColorKey = keyof typeof semanticColors.light;
export type GradientKey = keyof typeof gradients;